import { z } from "zod";
import {
  SharedApiError,
  normalizeApiBaseUrl,
  parseErrorResponse,
  type FetchLike,
} from "./shared";

export const presenceHeartbeatBodySchema = z.object({
  deviceType: z.enum(["desktop", "mobile", "web"]),
});

export const presenceWriteSuccessSchema = z.object({
  ok: z.literal(true),
});

async function parsePresenceWriteSuccess(
  response: Response,
  fallbackMessage: string,
): Promise<z.infer<typeof presenceWriteSuccessSchema>> {
  const payload =
    typeof response.json === "function"
      ? await response.json().catch(() => null)
      : null;
  const parsed = presenceWriteSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError(fallbackMessage, 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}

export function presenceHeartbeatPath(): string {
  return "/api/presence/heartbeat";
}

export function presenceDisconnectPath(): string {
  return "/api/presence/disconnect";
}

export async function presenceHeartbeatRequest({
  apiBaseUrl,
  accessToken,
  body,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  body: z.input<typeof presenceHeartbeatBodySchema>;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof presenceWriteSuccessSchema>> {
  const fetchFn = fetchImpl ?? fetch;
  const parsedBody = presenceHeartbeatBodySchema.parse(body);
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${presenceHeartbeatPath()}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(parsedBody),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to send heartbeat");
  }

  return parsePresenceWriteSuccess(response, "Invalid heartbeat response");
}

export async function presenceDisconnectRequest({
  apiBaseUrl,
  accessToken,
  body,
  fetchImpl,
  keepalive,
}: {
  apiBaseUrl: string;
  accessToken: string;
  body: z.input<typeof presenceHeartbeatBodySchema>;
  fetchImpl?: FetchLike;
  /**
   * Pass `true` to set the `keepalive` flag on the underlying request so it
   * survives a page unload. Used by browser clients that fire the disconnect
   * during `beforeunload`.
   */
  keepalive?: boolean;
}): Promise<z.infer<typeof presenceWriteSuccessSchema>> {
  const fetchFn = fetchImpl ?? fetch;
  const parsedBody = presenceHeartbeatBodySchema.parse(body);
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${presenceDisconnectPath()}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(parsedBody),
      ...(keepalive ? { keepalive: true } : {}),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to disconnect");
  }

  return parsePresenceWriteSuccess(response, "Invalid disconnect response");
}
